/**
 * go2rtc 영상 한 줄기를 `<video>` 에 붙인다. **React 를 모른다** — 붙이고 끊는 일만 한다.
 *
 * ## 왜 직접 짰는가 (의존성 0)
 *
 * go2rtc 는 `www/video-rtc.js`(웹 컴포넌트)를 같이 준다. 들이지 않은 이유:
 *   ① 타입이 없는 JS 700 줄을 통째로 들여야 하고, 이 리포는 `strict` + `verbatimModuleSyntax` 다
 *   ② 커스텀 엘리먼트라 React 밖에서 자기 `<video>` 를 만든다 — 경계 상태 다섯 가지를 우리가
 *      쥘 수 없다(연결 중 · 실패 사유 · 재시도를 그 안에서 콘솔로만 말한다)
 *   ③ 우리가 쓰지 않는 길(HLS · MJPEG · 마이크 송신 · 배경 재생)이 절반이다
 * 협상 자체는 **JSON 메시지 네 종류**뿐이라 아래 200 줄로 끝난다. 프로토콜은 그 파일(1.9.14)에서 읽었다.
 *
 * ## 길은 둘 — WebRTC 먼저, 안 되면 MSE
 *
 * 🔴 **「WebRTC 만」으로 만들지 않는다.** Tauri 리눅스 웹뷰(WebKitGTK)는 WebRTC 가 꺼진 빌드가
 *    흔하다 — 그 환경의 답이 MSE 다. 둘 다 없으면(= jsdom·구형 웹뷰) **죽지 않고 실패 상태로**
 *    떨어지고 화면이 사유를 적는다.
 *
 * ## 계약이 아니다
 *
 * ⚠ 이 프로토콜은 CTCells 계약(`orchestrator_api` · `events.asyncapi`)에 **없다.** 영상은 별도
 *   중계기(go2rtc)가 내고, 어느 모듈이 어느 카메라를 보는지도 서버가 말해 주지 않는다(BR-40).
 *   그래서 주소·이름은 전부 설정에서 온다(`@ctcells/core` `visionConfig`).
 */

export type VisionStreamMode = 'webrtc' | 'mse'

/** 실패 사유. 화면 문구는 이 값으로 고른다 — 여기서 한글을 만들지 않는다 */
export type VisionStreamReason =
  /** 이 웹뷰에 WebRTC 도 MSE 도 없다. 다시 시도해도 같다 → 재시도하지 않는다 */
  | 'unsupported'
  /** 연결이 끊겼다(중계기 정지 · 스트림 없음 · 망 단절) → 잠시 뒤 다시 붙는다 */
  | 'disconnected'

export interface VisionStreamState {
  phase: 'connecting' | 'playing' | 'failed'
  /** 어느 길로 붙었나. 진단에 필요해 화면에도 적는다 — 지어낸 값이 아니라 우리 연결의 사실이다 */
  mode: VisionStreamMode | undefined
  reason: VisionStreamReason | undefined
  /** 🔴 `<video>` 가 말해 주는 값만. 해상도를 설정이나 짐작으로 적지 않는다 */
  size: { width: number; height: number } | undefined
}

export interface VisionStreamOptions {
  url: string
  video: HTMLVideoElement
  /** WebRTC 협상 한도(ms). 넘으면 MSE 로 내려간다 */
  negotiateTimeoutMs: number
  /** 끊긴 뒤 다시 붙기까지(ms) */
  reconnectDelayMs: number
  onState: (state: VisionStreamState) => void
}

export interface VisionStreamHandle {
  /** 🔴 언마운트·화면 숨김에서 **반드시** 부른다. 안 끊으면 중계기에 소비자가 쌓인다 */
  close: () => void
}

/**
 * MSE 로 물어볼 코덱. go2rtc 가 이 중 자기가 낼 수 있는 것을 골라 되돌려준다.
 * ⚠ 목록 자체는 go2rtc `video-rtc.js` 의 것과 같다(Chromecast 세대별 프로필 + AAC/FLAC/OPUS).
 */
const CODECS = [
  'avc1.640029',
  'avc1.64002A',
  'avc1.640033',
  'hvc1.1.6.L153.B0',
  'mp4a.40.2',
  'mp4a.40.5',
  'flac',
  'opus',
] as const

/** 버퍼에 남겨 둘 길이(초). 라이브라 과거를 들고 있을 이유가 없다 */
const BUFFER_KEEP_SEC = 5

/**
 * 버퍼가 이만큼(초) 넘게 쌓이면 붙이기보다 **비우기를 먼저** 한다.
 * 🔴 비우기를 「대기열이 빌 때만」 하면 웹뷰가 바쁠 때 영영 못 비운다 — 아래 `MSE_MAX_PENDING_BYTES` 주석 참조
 */
const BUFFER_TRIM_AT_SEC = BUFFER_KEEP_SEC * 2

/** 재생 위치가 라이브 끝에서 이만큼(초) 넘게 뒤처지면 건너뛴다 */
const LIVE_MAX_LAG_SEC = 3
/** 건너뛸 때 라이브 끝에서 남길 거리(초) — 끝에 딱 붙이면 다음 조각을 기다리며 바로 멎는다 */
const LIVE_TARGET_LAG_SEC = 0.5
/** 건너뛰기 사이 최소 간격(ms) */
const LIVE_SEEK_GAP_MS = 5_000

/**
 * 아직 못 붙인 조각의 상한(바이트). 넘으면 이 연결을 버리고 새로 붙는다(처음 조각부터 다시 받는다).
 *
 * 🔴 2026-10-26 비전 PC(10.10.20.133 · arm64 · WebKitGTK) 설치 창의 `WebKitWebProcess` 가 **받는 영상 속도
 *    그대로(약 39MB/분) 끝없이 커졌다**(직접 원인은 아래 `trim` 주석의 메인 스레드 멈춤). 그와 별개로 옛 코드는 붙이기를
 *    `updateend` 한 번에 한 조각씩만 하고 비우기는 대기열이 빌 때만 해서, 붙이기가 조각 도착(초당 15)을 못 따라가면
 *    대기열과 버퍼가 함께 무한히 쌓이는 구조였다(이 PC WebKit 에서 붙이기를 느리게 모사해 재현 — 버퍼 0~111초 · 비우기 0회).
 *    그래서 ① 쌓인 조각은 **한 번에 이어 붙이고**(go2rtc `video-rtc.js` 와 같다) ② 버퍼가 길면 비우기를 먼저 하고
 *    ③ 그래도 못 따라가면 여기서 끊는다. 4Mbps 영상의 약 16초 분량이다(라이브라 그 이상 밀려 있으면 볼 가치도 없다).
 */
const MSE_MAX_PENDING_BYTES = 8 * 1024 * 1024

/**
 * 다시 붙기 간격의 상한(ms). 실패가 이어지면 간격을 두 배씩 늘려 여기서 멈춘다(eric 2026-10-21 「영상 연결 안되면.. 버튼이 안먹어..
 * 이런 랙이 없도록」) — 중계기가 죽어 있는 동안 영상 칸 넷이 3초마다 연결 · 해제를 되풀이하지 않게. 붙으면 처음 간격으로 돌아간다
 */
const MAX_RECONNECT_DELAY_MS = 30_000

export function connectVisionStream(options: VisionStreamOptions): VisionStreamHandle {
  const { video, url, onState } = options

  let disposed = false
  let socket: WebSocket | undefined
  let peer: RTCPeerConnection | undefined
  let objectUrl: string | undefined
  let negotiateTimer: ReturnType<typeof setTimeout> | undefined
  let retryTimer: ReturnType<typeof setTimeout> | undefined
  /** 붙지 못하고 이어진 실패 수 — 다시 붙기 간격을 늘린다. 붙으면 0 */
  let failures = 0
  let state: VisionStreamState = {
    phase: 'connecting',
    mode: undefined,
    reason: undefined,
    size: undefined,
  }

  function emit(patch: Partial<VisionStreamState>): void {
    if (disposed) return
    state = { ...state, ...patch }
    onState(state)
  }

  /** 지금 붙어 있는 것만 끊는다(재시도 여지는 남긴다) */
  function dropTransport(): void {
    if (negotiateTimer !== undefined) clearTimeout(negotiateTimer)
    negotiateTimer = undefined

    if (socket !== undefined) {
      socket.onopen = null
      socket.onmessage = null
      socket.onerror = null
      socket.onclose = null
      socket.close()
      socket = undefined
    }
    if (peer !== undefined) {
      peer.onicecandidate = null
      peer.ontrack = null
      peer.onconnectionstatechange = null
      peer.close()
      peer = undefined
    }
    setSrcObject(video, null)
    video.removeAttribute('src')
    if (objectUrl !== undefined) {
      URL.revokeObjectURL(objectUrl)
      objectUrl = undefined
    }
  }

  function fail(reason: VisionStreamReason): void {
    if (disposed) return
    dropTransport()
    emit({ phase: 'failed', mode: undefined, reason, size: undefined })
    // 🔴 지원 자체가 없으면 다시 붙어도 같다 — 무한 재시도로 중계기를 두드리지 않는다
    if (reason === 'unsupported') return
    failures += 1
    const delay = Math.min(options.reconnectDelayMs * 2 ** (failures - 1), MAX_RECONNECT_DELAY_MS)
    retryTimer = setTimeout(() => {
      retryTimer = undefined
      start()
    }, delay)
  }

  function markPlaying(mode: VisionStreamMode): void {
    failures = 0
    emit({
      phase: 'playing',
      mode,
      reason: undefined,
      size:
        video.videoWidth > 0 && video.videoHeight > 0
          ? { width: video.videoWidth, height: video.videoHeight }
          : undefined,
    })
  }

  /**
   * 자동 재생 정책: `muted` 라 통과하지만 거절될 수 있다 — 거절을 실패로 세지 않는다.
   * ⚠ `play()` 가 프로미스를 주지 않는 구현(jsdom)도 있어 반환값을 믿지 않는다.
   */
  function play(): void {
    try {
      const result: unknown = video.play()
      if (result instanceof Promise) void result.catch(() => undefined)
    } catch {
      // 재생 자체를 못 거는 환경 — 상태는 아래 이벤트가 말한다
    }
  }

  function openSocket(handlers: {
    onOpen: (ws: WebSocket) => void
    onJson: (message: { type: string; value?: unknown }) => void
    onBinary?: (data: ArrayBuffer) => void
    onGone: () => void
  }): WebSocket | undefined {
    const Ctor = globalScope().WebSocket
    if (typeof Ctor !== 'function') {
      fail('unsupported')
      return undefined
    }
    const ws = new Ctor(url)
    ws.binaryType = 'arraybuffer'
    ws.onopen = () => {
      handlers.onOpen(ws)
    }
    ws.onmessage = (event: MessageEvent) => {
      if (typeof event.data === 'string') {
        const parsed = parseJson(event.data)
        if (parsed !== undefined) handlers.onJson(parsed)
      } else if (handlers.onBinary !== undefined && event.data instanceof ArrayBuffer) {
        handlers.onBinary(event.data)
      }
    }
    ws.onerror = () => {
      handlers.onGone()
    }
    ws.onclose = () => {
      handlers.onGone()
    }
    return ws
  }

  // ── 길 ①: WebRTC ─────────────────────────────────────────────────────────
  function startWebRtc(): void {
    const Ctor = globalScope().RTCPeerConnection
    if (typeof Ctor !== 'function') {
      startMse()
      return
    }
    emit({ phase: 'connecting', mode: 'webrtc', reason: undefined, size: undefined })

    /*
     * 🔴 피어는 중계기가 «답한 뒤»에 만든다(eric 2026-10-21 「영상 연결 안되면.. 랙」) — 중계기가 죽어 있으면 소켓이 열리지도 않는데
     *    그때마다 피어를 만들고 닫는 일이 영상 칸마다 되풀이됐다. 소켓이 한 번도 안 열렸으면 MSE 로 내려가지 않고 바로 실패로 둔다 —
     *    MSE 도 같은 중계기 · 같은 주소라 같은 결과다(다음 시도는 늘어난 간격 뒤)
     */
    let opened = false
    socket = openSocket({
      onOpen: (ws) => {
        opened = true
        const pc = new Ctor({ bundlePolicy: 'max-bundle', iceServers: [] })
        peer = pc

        pc.addTransceiver('video', { direction: 'recvonly' })
        // 소리가 없는 카메라면 서버가 inactive 로 답한다 — 물어보는 것 자체는 해가 없다
        pc.addTransceiver('audio', { direction: 'recvonly' })

        pc.onicecandidate = (event) => {
          socket?.send(
            JSON.stringify({
              type: 'webrtc/candidate',
              value: event.candidate === null ? '' : event.candidate.candidate,
            }),
          )
        }
        pc.ontrack = (event) => {
          // 서버가 msid 를 붙여 주면 그 묶음을 쓰고, 아니면 우리가 묶는다
          const stream = event.streams[0] ?? makeStream(event.track)
          if (stream === undefined) return
          setSrcObject(video, stream)
          play()
        }
        pc.onconnectionstatechange = () => {
          if (pc.connectionState === 'connected') {
            if (negotiateTimer !== undefined) clearTimeout(negotiateTimer)
            negotiateTimer = undefined
            markPlaying('webrtc')
          } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
            // 붙은 적이 있으면 같은 길로 다시, 아니면 MSE 로 내려간다
            if (state.phase === 'playing') fail('disconnected')
            else fallbackToMse()
          }
        }

        void (async () => {
          try {
            const offer = await pc.createOffer()
            await pc.setLocalDescription(offer)
            ws.send(JSON.stringify({ type: 'webrtc/offer', value: offer.sdp }))
          } catch {
            fallbackToMse()
          }
        })()
      },
      onJson: (message) => {
        const pc = peer
        if (pc === undefined) return
        const value = typeof message.value === 'string' ? message.value : ''
        if (message.type === 'webrtc/answer') {
          void pc.setRemoteDescription({ type: 'answer', sdp: value }).catch(() => {
            fallbackToMse()
          })
        } else if (message.type === 'webrtc/candidate' && value !== '') {
          // go2rtc 는 sdpMid 를 주지 않는다. bundle 이라 `'0'` 하나뿐이다
          void pc.addIceCandidate({ candidate: value, sdpMid: '0' }).catch(() => undefined)
        } else if (message.type === 'error') {
          fallbackToMse()
        }
      },
      onGone: () => {
        if (state.phase === 'playing' || !opened) fail('disconnected')
        else fallbackToMse()
      },
    })

    negotiateTimer = setTimeout(() => {
      negotiateTimer = undefined
      if (state.phase !== 'playing') fallbackToMse()
    }, options.negotiateTimeoutMs)
  }

  function fallbackToMse(): void {
    if (disposed) return
    dropTransport()
    startMse()
  }

  // ── 길 ②: MSE (WebKitGTK 웹뷰의 답) ───────────────────────────────────────
  function startMse(): void {
    const Ctor = globalScope().MediaSource
    if (typeof Ctor !== 'function') {
      fail('unsupported')
      return
    }
    emit({ phase: 'connecting', mode: 'mse', reason: undefined, size: undefined })

    const media = new Ctor()
    objectUrl = URL.createObjectURL(media)
    video.srcObject = null
    video.src = objectUrl
    play()

    let buffer: SourceBuffer | undefined
    /** 아직 못 붙인 조각들. 붙일 때 **한 덩이로 이어** 한 번에 붙인다 */
    let pending: ArrayBuffer[] = []
    let pendingBytes = 0
    /** 마지막으로 라이브 끝으로 건너뛴 때(ms) — 건너뛰기도 seek 라 드물게만 한다 */
    let lastSeekAt = 0
    /** 바로 앞 일이 비우기였나 — 비우기와 붙이기를 번갈아 하게 한다 */
    let removedLast = false
    let socketOpen = false
    let sourceOpen = false

    const supported = CODECS.filter((codec) =>
      Ctor.isTypeSupported(`video/mp4; codecs="${codec}"`),
    ).join(',')

    const ask = (): void => {
      if (!socketOpen || !sourceOpen) return
      socket?.send(JSON.stringify({ type: 'mse', value: supported }))
    }

    /**
     * 버퍼가 놀고 있을 때 할 일 하나를 건다 — 버퍼가 길면 비우기, 아니면 쌓인 조각 전부를 한 번에 붙이기.
     * 🔴 「한 조각씩」 붙이지 않는다. 그러면 붙이기 횟수가 도착 횟수를 따라가야 하는데 바쁜 웹뷰는 못 따라간다(위 상수 주석)
     */
    const flush = (): void => {
      const sb = buffer
      if (sb === undefined || sb.updating) return
      // 🔴 비우기를 두 번 잇달아 걸지 않는다 — 웹뷰가 그 구간을 못 지우면(재생 위치 근처 등) 비우기만 돌고 붙이기가 굶는다
      if (!removedLast && trim(sb, pending.length === 0 ? BUFFER_KEEP_SEC : BUFFER_TRIM_AT_SEC)) {
        removedLast = true
        return
      }
      removedLast = false
      if (pending.length === 0) return
      const chunk = concat(pending, pendingBytes)
      pending = []
      pendingBytes = 0
      try {
        sb.appendBuffer(chunk)
      } catch {
        // 버퍼가 꽉 차거나 닫힌 뒤의 append — 그 덩이는 버리고 다음 조각부터 간다
      }
    }

    media.addEventListener(
      'sourceopen',
      () => {
        sourceOpen = true
        ask()
      },
      { once: true },
    )

    socket = openSocket({
      onOpen: () => {
        socketOpen = true
        ask()
      },
      onJson: (message) => {
        /*
         * 🔴 go2rtc 는 «없는 스트림»(`mse: streams: 404 Not Found`)을 error 한 줄로 답하고 소켓을 열어 둔다(2026-10-26 실측 ·
         *    `ir_right`). 이걸 흘리면 close 가 안 와서 「연결 중」에 영원히 선다 — 실패로 접고 늘어나는 간격으로 다시 붙는다
         */
        if (message.type === 'error') {
          fail('disconnected')
          return
        }
        if (message.type !== 'mse' || typeof message.value !== 'string') return
        try {
          const sb = media.addSourceBuffer(message.value)
          sb.mode = 'segments'
          sb.addEventListener('updateend', flush)
          buffer = sb
          markPlaying('mse')
        } catch {
          fail('disconnected')
        }
      },
      onBinary: (data) => {
        pending.push(data)
        pendingBytes += data.byteLength
        if (pendingBytes > MSE_MAX_PENDING_BYTES) {
          // 웹뷰가 붙이기를 못 따라간다 — 쌓아 두지 않고 새로 붙는다(메모리 상한 · 위 상수 주석)
          fail('disconnected')
          return
        }
        flush()
      },
      onGone: () => {
        fail('disconnected')
      },
    })

    /**
     * 과거 구간을 버리고, 재생 위치가 많이 뒤처졌으면 라이브 끝 근처로 한 번 건너뛴다.
     * 버퍼가 `trimAtSec` 보다 길 때만 비운다. **비우기를 걸었으면 `true`** — 그 `updateend` 에서 붙이기를 이어 한다.
     *
     * 🔴 `playbackRate` 를 건드리지 않는다. go2rtc `video-rtc.js` 는 조각마다(초당 ~15번) 재생 속도를 간격에 맞춰 바꾸는데,
     *    WebKitGTK 에서 재생 속도 변경은 메인 스레드가 GStreamer 파이프라인에 seek 를 거는 일이다. 2026-10-26 비전 PC 염색 창의
     *    `WebKitWebProcess` 메인 스레드가 futex 에서 멎었고(CPU · 문맥 전환 0, 디코더 스레드도 대기) 그동안 받은 영상이
     *    수신 큐에 받는 속도 그대로 쌓였다. 건너뛰기도 seek 라 `LIVE_SEEK_GAP_MS` 에 한 번까지만 한다.
     */
    function trim(sb: SourceBuffer, trimAtSec: number): boolean {
      if (sb.buffered.length === 0) return false
      const end = sb.buffered.end(sb.buffered.length - 1)
      const keepFrom = end - BUFFER_KEEP_SEC
      const first = sb.buffered.start(0)
      const now = Date.now()
      if (end - video.currentTime > LIVE_MAX_LAG_SEC && now - lastSeekAt >= LIVE_SEEK_GAP_MS) {
        lastSeekAt = now
        video.currentTime = Math.max(first, end - LIVE_TARGET_LAG_SEC)
      }
      if (state.phase === 'playing' && state.size === undefined) markPlaying('mse')
      if (end - first <= trimAtSec || keepFrom <= first) return false
      try {
        sb.remove(first, keepFrom)
        return true
      } catch {
        // 제거를 못 거는 때(닫힘 등) — 붙이기는 그대로 한다
        return false
      }
    }
  }

  function start(): void {
    if (disposed) return
    startWebRtc()
  }

  // `<video>` 가 스스로 말해 주는 것만 받아 적는다
  const onLoaded = (): void => {
    if (state.phase === 'playing' && state.mode !== undefined) markPlaying(state.mode)
  }
  video.addEventListener('loadeddata', onLoaded)
  video.addEventListener('resize', onLoaded)

  start()

  return {
    close: () => {
      disposed = true
      if (retryTimer !== undefined) clearTimeout(retryTimer)
      retryTimer = undefined
      video.removeEventListener('loadeddata', onLoaded)
      video.removeEventListener('resize', onLoaded)
      dropTransport()
    },
  }
}

/**
 * 생성자를 **부를 때** 전역에서 꺼낸다 — 모듈 최상단에서 구조분해하면 없는 환경(jsdom·구형 웹뷰)에서
 * 불러오는 순간 죽고, 테스트가 대역을 끼울 자리도 사라진다.
 */
interface VisionGlobals {
  WebSocket?: typeof WebSocket
  RTCPeerConnection?: typeof RTCPeerConnection
  MediaSource?: typeof MediaSource
  MediaStream?: typeof MediaStream
}

function globalScope(): VisionGlobals {
  // 타입상으로는 전부 있다고 되어 있지만 **실제로 없는 환경이 있다** — 그래서 선택 속성으로 받는다
  return globalThis
}

/** `srcObject` 가 없는 구현(jsdom)에서 죽지 않는다 */
function setSrcObject(video: HTMLVideoElement, value: MediaStream | null): void {
  try {
    video.srcObject = value
  } catch {
    // 이 환경은 MediaStream 재생을 못 한다 — 아래 MSE 길이 답이다
  }
}

/** 조각들을 한 덩이로 잇는다. 하나뿐이면 복사하지 않는다 */
function concat(chunks: readonly ArrayBuffer[], total: number): ArrayBuffer {
  const only = chunks.length === 1 ? chunks[0] : undefined
  if (only !== undefined) return only
  const out = new Uint8Array(total)
  let at = 0
  for (const chunk of chunks) {
    out.set(new Uint8Array(chunk), at)
    at += chunk.byteLength
  }
  return out.buffer
}

function makeStream(track: MediaStreamTrack): MediaStream | undefined {
  const Ctor = globalScope().MediaStream
  return typeof Ctor === 'function' ? new Ctor([track]) : undefined
}

function parseJson(raw: string): { type: string; value?: unknown } | undefined {
  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return undefined
    const { type, value } = parsed as { type?: unknown; value?: unknown }
    return typeof type === 'string' ? { type, value } : undefined
  } catch {
    return undefined
  }
}
